import { Navigate } from "react-router-dom";

import { useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import ApiTokensView from "~/features/apiTokens/ApiTokensView";

import { BatchesSettingsPage } from "~/features/preview/batches/BatchesSettingsPage";
import { BinsSettingsPage } from "~/features/preview/bins/BinsSettingsPage";
import { GeneralSettingsPage } from "~/features/preview/general/GeneralSettingsPage";
import { SettingsWorkstationPage } from "~/features/preview/workstation/SettingsWorkstationPage";
import { FcNavigate } from "~/hooks/useFcNavigate";
import { useNavbar } from "~/hooks/useNavbar";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { selectIfWorkstationsTabShouldBeEnabled } from "~/redux/selectors/workstationsSelectors";

import SettingsView from "./SettingsView";

function LegacyTabRedirect({ search }: { search: string }) {
  return <Navigate to={{ pathname: "/settings", search }} replace />;
}

export function SettingsRootRoute() {
  const { enableSideNavSubMenus } = useFlag();
  if (enableSideNavSubMenus) {
    return <FcNavigate to="/settings/general" />;
  }
  return <SettingsView viewTitle="nav.viewname.settings" />;
}

export function SettingsGeneralSectionRoute() {
  const { enableSideNavSubMenus } = useFlag();
  if (!enableSideNavSubMenus) {
    return <LegacyTabRedirect search="?settings-tab=settings" />;
  }
  return <SettingsGeneralSection />;
}

function SettingsGeneralSection() {
  return <GeneralSettingsPage viewTitle="nav.viewname.settings general" />;
}

export function SettingsBinsSectionRoute() {
  const { enableSideNavSubMenus } = useFlag();
  const isAdmin = useAppSelector(selectUserIsAdmin) || false;
  if (!enableSideNavSubMenus) {
    return <LegacyTabRedirect search="?settings-tab=settings" />;
  }
  if (!isAdmin) {
    return <FcNavigate to="/settings/general" />;
  }
  return <SettingsBinsSection />;
}

function SettingsBinsSection() {
  return <BinsSettingsPage viewTitle="nav.viewname.settings bins" />;
}

export function SettingsWorkstationSectionRoute() {
  const { enableSideNavSubMenus } = useFlag();
  const shouldEnable = useAppSelector(selectIfWorkstationsTabShouldBeEnabled);
  if (!enableSideNavSubMenus) {
    return <LegacyTabRedirect search="?settings-tab=workstation-config" />;
  }
  if (!shouldEnable) {
    return <FcNavigate to="/settings/general" />;
  }
  return <SettingsWorkstationSection />;
}

function SettingsWorkstationSection() {
  return (
    <SettingsWorkstationPage viewTitle="nav.viewname.settings workstation preview" />
  );
}

export function SettingsBatchSectionRoute() {
  const { enableSideNavSubMenus } = useFlag();
  const isAdmin = useAppSelector(selectUserIsAdmin) || false;
  if (!enableSideNavSubMenus) {
    return <LegacyTabRedirect search="?settings-tab=batch-settings" />;
  }
  if (!isAdmin) {
    return <FcNavigate to="/settings/general" />;
  }
  return <SettingsBatchSection />;
}

function SettingsBatchSection() {
  return <BatchesSettingsPage viewTitle="nav.viewname.settings batches" />;
}

export function SettingsGridSectionRoute() {
  const { enableSideNavSubMenus } = useFlag();
  if (!enableSideNavSubMenus) {
    return <LegacyTabRedirect search="?settings-tab=grid-settings" />;
  }
  return <FcNavigate to="/settings/general" />;
}

export function SettingsApiTokensSectionRoute() {
  const { enableSideNavSubMenus, enableApiKeysView } = useFlag();
  const isAdmin = useAppSelector(selectUserIsAdmin) || false;
  const allowed = isAdmin && enableApiKeysView;
  if (!enableSideNavSubMenus) {
    return <LegacyTabRedirect search="?settings-tab=api-tokens" />;
  }
  if (!allowed) {
    return <FcNavigate to="/settings/general" />;
  }
  return <SettingsApiTokensSection />;
}

function SettingsApiTokensSection() {
  useNavbar({ viewTitle: "nav.viewname.settings" });
  return <ApiTokensView />;
}
